import {
  READINESS_READY_CUBE_BORDER,
  READINESS_READY_CUBE_GRADIENT,
} from './checklistBrandColors';
import { EMAIL_CUBES_DATA_URI, EMAIL_LOGO_WHITE_DATA_URI } from './checklistSalesEmailAssets';
import { EMAIL_FONT_BODY, EMAIL_FONT_MONO } from './checklistSalesEmailFonts';

/** @typedef {string | { src: string, width?: number, height?: number }} HeadingImageValue */
import { buildDiscussTaskUrl } from './discussTaskLinks';

/** Простой линейный градиент — Yandex Mail режет radial/multi-layer background. */
const HEADER_GRADIENT = 'linear-gradient(90deg,#EA380E 0%,#F08517 42%,#FFBF2A 100%)';
const HEADER_FALLBACK = '#F08517';
const CTA_GRADIENT = HEADER_GRADIENT;
const CTA_FALLBACK = HEADER_FALLBACK;

const FONT = EMAIL_FONT_BODY;
const MONO = EMAIL_FONT_MONO;

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function resolveHeadingImage(value) {
  if (!value) return null;
  if (typeof value === 'string') {
    return { src: value };
  }
  return value.src ? value : null;
}

function headingImageHtml(imageValue, alt, fallbackHtml) {
  const image = resolveHeadingImage(imageValue);
  if (!image?.src) {
    return fallbackHtml;
  }

  const widthAttr = image.width ? ` width="${image.width}"` : '';
  const heightAttr = image.height ? ` height="${image.height}"` : '';

  return `<img src="${image.src}" alt="${escapeHtml(alt)}"${widthAttr}${heightAttr} style="display:block;border:0;max-width:100%;height:auto;margin:0 auto;" />`;
}

function gradientStyle(fallbackColor, gradient) {
  return `background-color:${fallbackColor};background-image:${gradient};`;
}

function readinessHeaderBadgeStyle(label, variant = 'sales') {
  const readyLabels =
    variant === 'operations' ? ['Можно планировать пилот'] : ['Можно внедрять'];
  const partialLabels =
    variant === 'operations'
      ? ['Пилот с ограничениями']
      : ['Частично готовы'];

  if (readyLabels.includes(label)) {
    return 'background-color:rgba(255,255,255,0.22);border:1px solid rgba(255,255,255,0.45);color:#ffffff;';
  }
  if (partialLabels.includes(label)) {
    return 'background-color:rgba(255,255,255,0.2);border:1px solid rgba(227,175,32,0.3);color:#ffffff;';
  }
  return 'background-color:rgba(255,255,255,0.15);border:1px solid rgba(255,255,255,0.25);color:#ffffff;';
}

/** Баллы в шапке письма — только HTML (PNG обрезает цифры в почтовых клиентах). */
function scoreHeaderHtml(score, maxScore) {
  return `<table cellpadding="0" cellspacing="0" align="center" style="margin:32px auto 0;">
  <tr>
    <td align="center" style="padding:6px 0 10px;font-family:${MONO};font-size:72px;line-height:80px;mso-line-height-rule:exactly;color:#ffffff;white-space:nowrap;">
      <span style="display:inline-block;vertical-align:baseline;line-height:80px;">${score}</span><span style="display:inline-block;vertical-align:baseline;margin-left:6px;font-size:36px;line-height:80px;color:rgba(255,255,255,0.55);">/ ${maxScore}</span>
    </td>
  </tr>
</table>`;
}

function readinessBadgeHeaderHtml(label, emoji, variant = 'sales') {
  return `<table cellpadding="0" cellspacing="0" align="center" style="margin:32px auto 0;">
  <tr>
    <td align="center" style="padding:8px 20px;border-radius:8px;font-size:16px;line-height:24px;font-family:${FONT};font-weight:500;white-space:nowrap;${readinessHeaderBadgeStyle(label, variant)}">
      ${emoji} ${escapeHtml(label)}
    </td>
  </tr>
</table>`;
}

function progressBarHtml(score, maxScore) {
  const percent = maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;
  return `
<table cellpadding="0" cellspacing="0" width="100%" style="margin-top:12px;">
  <tr>
    <td bgcolor="#f3f4f6" style="background-color:#f3f4f6;border-radius:999px;height:8px;padding:0;font-size:0;line-height:0;">
      <table cellpadding="0" cellspacing="0" width="${percent}%">
        <tr>
          <td bgcolor="#ec5511" style="background-color:#ec5511;background-image:linear-gradient(90deg,#ec5511 0%,#ffbe2b 100%);border-radius:999px;height:8px;font-size:0;line-height:0;">&nbsp;</td>
        </tr>
      </table>
    </td>
  </tr>
</table>`;
}

function questionDotHtml(score) {
  const outer = score === 0 ? '#f3f4f6' : '#fef3c6';
  const inner = score === 0 ? '#99a1af' : '#e17100';
  return `<span style="display:inline-block;width:20px;height:20px;border-radius:999px;background-color:${outer};text-align:center;line-height:20px;vertical-align:top;">
    <span style="display:inline-block;width:8px;height:8px;border-radius:999px;background-color:${inner};margin-top:6px;"></span>
  </span>`;
}

function blockCardHtml(block) {
  const questions = block.questions
    .map(
      (q) => `
<tr>
  <td style="padding:0 0 16px 0;vertical-align:top;width:28px;">${questionDotHtml(q.score)}</td>
  <td style="padding:0 0 16px 0;vertical-align:top;">
    <p style="margin:0;font-size:16px;line-height:24px;color:#364153;font-family:${FONT};">${escapeHtml(q.text)}</p>
    ${
      q.answerText
        ? `<p style="margin:6px 0 0;font-size:14px;line-height:20px;color:#99a1af;font-family:${FONT};">${escapeHtml(q.answerText)}</p>`
        : `<p style="margin:6px 0 0;font-size:14px;line-height:20px;color:#99a1af;font-family:${FONT};">${q.score} / ${q.maxScore} баллов</p>`
    }
  </td>
</tr>`,
    )
    .join('');

  const scoreCell =
    block.maxScore > 0
      ? `${block.score}<span style="font-size:16px;color:#99a1af;">/${block.maxScore}</span>`
      : '';
  const progressRow =
    block.maxScore > 0
      ? `<tr><td style="padding:20px 32px 0 32px;">${progressBarHtml(block.score, block.maxScore)}</td></tr>`
      : '';

  return `
<table cellpadding="0" cellspacing="0" width="100%" style="margin-bottom:32px;border:1px solid #e5e7eb;border-radius:14px;background-color:#ffffff;">
  <tr>
    <td bgcolor="#1a1a1a" style="background-color:#1a1a1a;padding:20px 32px;border-bottom:1px solid #f3f4f6;">
      <table cellpadding="0" cellspacing="0" width="100%">
        <tr>
          <td style="font-family:${MONO};font-size:20px;line-height:28px;color:#ffffff;font-weight:bold;">${escapeHtml(block.title)}</td>
          ${
            scoreCell
              ? `<td align="right" style="font-family:${MONO};font-size:24px;line-height:32px;color:#ffffff;white-space:nowrap;">${scoreCell}</td>`
              : ''
          }
        </tr>
      </table>
      <p style="margin:8px 0 0;font-size:14px;line-height:20px;color:#99a1af;font-family:${FONT};">${escapeHtml(block.description)}</p>
    </td>
  </tr>
  ${progressRow}
  <tr>
    <td style="padding:24px 32px 32px 32px;">
      <table cellpadding="0" cellspacing="0" width="100%">${questions}</table>
    </td>
  </tr>
</table>`;
}

function readinessScaleRowHtml(item) {
  if (item.active) {
    const isReady = item.id === 'ready' || item.id === 'pilot-ready';
    const cellStyle = isReady
      ? `${gradientStyle('#7CB342', READINESS_READY_CUBE_GRADIENT)}border:1px solid ${READINESS_READY_CUBE_BORDER};`
      : 'background-color:#ec5811;border:1px solid rgba(236,88,17,0.2);';
    const bgcolor = isReady ? '#7CB342' : '#ec5811';
    return `
<table cellpadding="0" cellspacing="0" width="100%" style="margin-bottom:16px;">
  <tr>
    <td bgcolor="${bgcolor}" style="${cellStyle}border-radius:10px;padding:16px 24px;">
      <table cellpadding="0" cellspacing="0" width="100%">
        <tr>
          <td style="width:36px;font-size:24px;line-height:32px;vertical-align:middle;">${item.emoji}</td>
          <td style="vertical-align:middle;">
            <p style="margin:0;font-size:16px;line-height:24px;color:#ffffff;font-family:${FONT};">${escapeHtml(item.label)}</p>
            <p style="margin:0;font-size:14px;line-height:20px;color:rgba(255,255,255,0.6);font-family:${FONT};">${escapeHtml(item.range)}</p>
          </td>
          <td align="right" style="width:20px;vertical-align:middle;">
            <span style="display:inline-block;width:10px;height:10px;border-radius:999px;background-color:#ffffff;">&nbsp;</span>
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>`;
  }

  return `
<table cellpadding="0" cellspacing="0" width="100%" style="margin-bottom:16px;">
  <tr>
    <td style="border:1px solid #e5e7eb;border-radius:10px;background-color:#ffffff;padding:16px 24px;">
      <table cellpadding="0" cellspacing="0" width="100%">
        <tr>
          <td style="width:36px;font-size:24px;line-height:32px;vertical-align:middle;">${item.emoji}</td>
          <td style="vertical-align:middle;">
            <p style="margin:0;font-size:16px;line-height:24px;color:#101828;font-family:${FONT};">${escapeHtml(item.label)}</p>
            <p style="margin:0;font-size:14px;line-height:20px;color:#6a7282;font-family:${FONT};">${escapeHtml(item.range)}</p>
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>`;
}

function pilotRowHtml(pilot, headingImages) {
  if (!pilot) return '';

  const bullets = pilot.bullets
    .map(
      (bullet) => `
<tr>
  <td style="padding:20px 0 0 0;vertical-align:top;width:16px;">
    <span style="display:inline-block;width:6px;height:6px;border-radius:999px;background-color:#1a1a1a;margin-top:10px;">&nbsp;</span>
  </td>
  <td style="padding:20px 0 0 8px;font-size:16px;line-height:28px;color:#1a1a1a;font-family:${FONT};">${escapeHtml(bullet)}</td>
</tr>`,
    )
    .join('');

  const linkHtml = pilot.linkHref
    ? `<p style="margin:32px 0 0;padding-top:32px;border-top:1px solid #f3f4f6;font-size:16px;line-height:24px;font-family:${FONT};">
        <a href="${escapeHtml(pilot.linkHref)}" style="color:#ec5612;text-decoration:none;font-weight:600;">${escapeHtml(pilot.linkLabel)}</a>
      </p>`
    : '';

  return `
<tr>
  <td bgcolor="#ECEBF2" style="background-color:#ECEBF2;border-top:1px solid rgba(26,26,26,0.2);padding:56px 40px;">
    <table cellpadding="0" cellspacing="0" width="100%" style="border:1px solid #e5e7eb;border-radius:14px;background-color:#ffffff;">
      <tr>
        <td style="padding:40px;">
          ${headingImageHtml(
            headingImages?.pilotTitle,
            pilot.title,
            `<h2 style="margin:0;font-family:${MONO};font-size:20px;line-height:28px;color:#1a1a1a;">${escapeHtml(pilot.title)}</h2>`,
          )}
          <p style="margin:16px 0 0;font-size:18px;line-height:28px;font-weight:600;color:#ec5612;font-family:${FONT};">${escapeHtml(pilot.solutionType)}</p>
          <p style="margin:16px 0 0;font-size:16px;line-height:28px;color:#1a1a1a;font-family:${FONT};">${escapeHtml(pilot.summary)}</p>
          <table cellpadding="0" cellspacing="0" width="100%" style="margin-top:32px;border-top:1px solid #f3f4f6;">${bullets}</table>
          ${linkHtml}
        </td>
      </tr>
    </table>
  </td>
</tr>`;
}

/**
 * HTML-вёрстка письма с inline-стилями (совместимость с почтовыми клиентами).
 */
export function buildChecklistSalesEmailHtmlContent(data, baseUrl, headingImages = {}, options = {}) {
  const d = data;
  const emailTestId = options.emailTestId || 'checklist-sales-email';
  const readinessVariant = options.readinessVariant || 'sales';
  const logoSrc = EMAIL_LOGO_WHITE_DATA_URI;
  const cubesSrc = EMAIL_CUBES_DATA_URI;
  const discussUrl = buildDiscussTaskUrl({
    score: d.score,
    maxScore: d.maxScore,
    origin: baseUrl,
  });

  const blocksHtml = d.blocks.map((block) => blockCardHtml(block)).join('');
  const readinessScaleHtml = d.readinessScale.map((item) => readinessScaleRowHtml(item)).join('');
  const headerBgStyle = gradientStyle(HEADER_FALLBACK, HEADER_GRADIENT);
  const ctaBgStyle = gradientStyle(CTA_FALLBACK, CTA_GRADIENT);

  const headerTitleHtml = headingImageHtml(
    headingImages.title,
    d.title,
    `<table cellpadding="0" cellspacing="0" align="center" width="640" style="max-width:640px;width:100%;margin:16px auto 0;">
      <tr>
        <td align="center" style="text-align:center;color:#ffffff;font-family:${MONO};font-size:30px;line-height:36px;font-weight:bold;letter-spacing:-0.53px;">
          ${escapeHtml(d.title)}
        </td>
      </tr>
    </table>`,
  );

  const scoreHtml = scoreHeaderHtml(d.score, d.maxScore);

  const readinessBadgeHtml = readinessBadgeHeaderHtml(
    d.readinessLabel,
    d.readinessEmoji,
    readinessVariant,
  );

  const blocksHeadingHtml = headingImageHtml(
    headingImages.blocksHeading,
    'Результаты по блокам',
    `<h2 style="margin:0;font-family:${MONO};font-size:24px;line-height:32px;color:#101828;">Результаты по блокам</h2>`,
  );

  const readinessHeadingHtml = headingImageHtml(
    headingImages.readinessHeading,
    'Шкала готовности',
    `<h2 style="margin:0;font-size:20px;line-height:28px;color:#101828;font-family:${FONT};font-weight:600;">Шкала готовности</h2>`,
  );

  const ctaTitleHtml = headingImageHtml(
    headingImages.ctaTitle,
    d.cta.title,
    `<h3 style="margin:0;font-family:${MONO};font-size:30px;line-height:36px;font-weight:bold;color:#ffffff;">${escapeHtml(d.cta.title)}</h3>`,
  );

  return `
<table cellpadding="0" cellspacing="0" width="100%" style="background-color:#f3f4f6;margin:0;padding:24px 0;">
  <tr>
    <td align="center">
      <table cellpadding="0" cellspacing="0" width="800" data-testid="${emailTestId}" style="width:100%;max-width:800px;background-color:#ffffff;">
        <tr>
          <td bgcolor="${HEADER_FALLBACK}" style="${headerBgStyle}padding:48px 40px;text-align:center;color:#ffffff;">
            <p style="margin:0;font-size:16px;line-height:24px;font-family:${FONT};color:#ffffff;">${escapeHtml(d.eyebrow)}</p>
            <table cellpadding="0" cellspacing="0" align="center" width="640" style="max-width:640px;width:100%;margin:16px auto 0;">
              <tr>
                <td align="center" style="text-align:center;">
                  ${headerTitleHtml}
                </td>
              </tr>
            </table>
            <table cellpadding="0" cellspacing="0" align="center" width="100%" style="margin:32px auto 0;">
              <tr>
                <td align="center">${scoreHtml}</td>
              </tr>
            </table>
            <table cellpadding="0" cellspacing="0" align="center" width="100%" style="margin:32px auto 0;">
              <tr>
                <td align="center">${readinessBadgeHtml}</td>
              </tr>
            </table>
          </td>
        </tr>
        <tr>
          <td bgcolor="#ECEBF2" style="background-color:#ECEBF2;border-bottom:1px solid rgba(26,26,26,0.2);padding:28px 40px;">
            <table cellpadding="0" cellspacing="0" width="100%">
              ${[
                ['Имя', d.user.name],
                ['Компания', d.user.company],
                ['Почта', d.user.email],
              ]
                .map(
                  ([label, value], index) => `
              <tr>
                <td style="padding:${index === 0 ? '0' : '20px'} 0 0 0;font-family:${MONO};font-size:18px;line-height:28px;color:#1a1a1a;font-weight:bold;width:40%;">${escapeHtml(label)}</td>
                <td align="right" style="padding:${index === 0 ? '0' : '20px'} 0 0 0;font-family:${FONT};font-size:18px;line-height:28px;font-weight:400;color:#101828;">${escapeHtml(value)}</td>
              </tr>`,
                )
                .join('')}
            </table>
          </td>
        </tr>
        <tr>
          <td bgcolor="#ECEBF2" style="background-color:#ECEBF2;padding:56px 40px;">
            ${blocksHeadingHtml}
            <table cellpadding="0" cellspacing="0" width="100%" style="margin-top:40px;"><tr><td>${blocksHtml}</td></tr></table>
          </td>
        </tr>
        ${pilotRowHtml(d.pilot, headingImages)}
        <tr>
          <td bgcolor="#ECEBF2" style="background-color:#ECEBF2;border-top:1px solid rgba(26,26,26,0.2);padding:48px 40px;">
            ${readinessHeadingHtml}
            <table cellpadding="0" cellspacing="0" width="100%" style="margin-top:32px;"><tr><td>${readinessScaleHtml}</td></tr></table>
          </td>
        </tr>
        <tr>
          <td bgcolor="#ECEBF2" style="background-color:#ECEBF2;padding:56px 40px;">
            <table cellpadding="0" cellspacing="0" width="100%" style="${ctaBgStyle}border-radius:14px;">
              <tr>
                <td bgcolor="${CTA_FALLBACK}" style="${ctaBgStyle}padding:40px;text-align:center;color:#ffffff;border-radius:14px;">
                  ${ctaTitleHtml}
                  <p style="margin:16px auto 0;max-width:360px;font-size:16px;line-height:24px;color:#f3e8ff;font-family:${FONT};">${escapeHtml(d.cta.subtitle)}</p>
                  <table cellpadding="0" cellspacing="0" align="center" style="margin:32px auto 0;">
                    <tr>
                      <td bgcolor="#000000" style="background-color:#000000;border-radius:10px;">
                        <a href="${escapeHtml(discussUrl)}" style="display:inline-block;padding:12px 32px;font-size:16px;line-height:24px;color:#ffffff;text-decoration:none;font-family:${FONT};font-weight:500;">${escapeHtml(d.cta.buttonLabel)}</a>
                      </td>
                    </tr>
                  </table>
                </td>
              </tr>
            </table>
          </td>
        </tr>
        <tr>
          <td bgcolor="#1a1a1a" style="background-color:#1a1a1a;padding:40px;border-top:1px solid #f3f4f6;">
            <table cellpadding="0" cellspacing="0" width="100%" style="min-height:140px;">
              <tr>
                <td width="58%" valign="bottom" style="vertical-align:bottom;padding:0 16px 0 0;">
                  <img src="${logoSrc}" alt="Антология" width="166" height="28" style="display:block;width:166px;height:28px;border:0;" />
                  <p style="margin:16px 0 0;font-size:14px;line-height:20px;color:#6a7282;font-family:${FONT};">
                    Выгодный и удобный<br />подбор ИТ‑инструментов
                  </p>
                  <a href="https://antologia.tech" style="display:inline-block;margin-top:8px;font-size:14px;line-height:20px;color:#4a5565;text-decoration:none;font-family:${FONT};">antologia.tech</a>
                </td>
                <td width="42%" align="right" valign="bottom" style="vertical-align:bottom;padding:0;line-height:0;font-size:0;">
                  <img src="${cubesSrc}" alt="" width="138" height="112" style="display:block;width:138px;height:112px;border:0;margin-left:auto;" />
                </td>
              </tr>
            </table>
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>`;
}
